import { ServiceException } from '@inter-ikea-kompis/exceptions';
import IframeManager from '../../utilities/IframeManager.js';
/**
 * This class is used for setting and getting store id.
 *
 * When the INGKA Javascript API is available on the same page, we can use it directly.
 * This is the case when the footer fragment from INGKA is included using ESI include, or if INGKA includes the planner directly on the page using ESI include.
 *
 * When a planner is running under /addon-app/, it is not possible to include the footer fragment from INGKA as we are not under a retail unit path (e.g. /gb/en/).
 * Then we can use an iframe with a URL that contains a retail unit path to load the INGKA Javascript API.
 */
export default class OneWebStoreIdCookieInformationService {
    constructor(options) {
        this.endpoint = options.endpoint;
        this.cacheStore = options.cacheStore;
    }
    /**
     * Returns store id.
     *
     * @returns Store id.
     */
    async getStoreId() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (window.ikea?.store?.get) {
            return await this.getIngkaApi(window).get();
        }
        const iframe = await this.getIframe();
        return await this.getIngkaApi(iframe.contentWindow).get();
    }
    /**
     * Sets store id.
     *
     * @param storeId Store id.
     */
    async setStoreId(storeId) {
        if (!storeId) {
            return await this.clearStoreId();
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (window.ikea?.store?.set) {
            await this.getIngkaApi(window).set(storeId);
            return;
        }
        const iframe = await this.getIframe();
        await this.getIngkaApi(iframe.contentWindow).set(storeId);
    }
    /**
     * Clears store id.
     */
    async clearStoreId() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (window.ikea?.store?.clear) {
            await this.getIngkaApi(window).clear();
            return;
        }
        const iframe = await this.getIframe();
        await this.getIngkaApi(iframe.contentWindow).clear();
    }
    /**
     * Returns INGKA store Javascript API.
     *
     * @param targetWindow Window.
     * @returns INGKA API.
     */
    getIngkaApi(targetWindow) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!targetWindow.ikea?.store) {
            throw new ServiceException('Failed to retrieve store API. "ikea.store" is not defined.');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return targetWindow.ikea.store;
    }
    /**
     * Returns iframe element.
     *
     * @returns Iframe.
     */
    async getIframe() {
        if (this.iframeManager && this.iframeManager.url !== this.endpoint) {
            this.iframeManager.disconnect();
            this.iframeManager = null;
        }
        if (!this.iframeManager) {
            this.iframeManager = new IframeManager(this.endpoint, this.cacheStore);
        }
        return await this.iframeManager.getIframe();
    }
}
//# sourceMappingURL=OneWebStoreIdCookieInformationService.js.map